import React, { useState, useEffect, useRef, useMemo } from "react";
import DownArrow from "../../resources/icons/chevron-down.svg";

const Dropdown = ({
  options,
  selected: initialSelected,
  onSelect,
  buttonClass,
  menuClass,
  isSearchable = false,
  searchPlaceholder = "Search...",
  testId = "dropdown",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState(initialSelected);
  const [searchInput, setSearchInput] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [alignEnd, setAlignEnd] = useState(false);
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);
  const searchInputRef = useRef(null);
  const optionRefs = useRef([]);
  const menuRef = useRef(null);

  const filteredOptions = useMemo(() => {
    if (!isSearchable || !searchInput.trim()) {
      return options;
    }

    const lowerSearchInput = searchInput.toLowerCase();
    return options.filter((option) =>
      option.name.toLowerCase().includes(lowerSearchInput),
    );
  }, [options, searchInput, isSearchable]);

  const onSelectRef = useRef(onSelect);

  useEffect(() => {
    onSelectRef.current = onSelect;
  }, [onSelect]);

  useEffect(() => {
    setSelected(initialSelected);
  }, [initialSelected]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchInput("");
        setHighlightedIndex(-1);
      }
    };

    window.addEventListener("click", handleClickOutside);

    return () => {
      window.removeEventListener("click", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (isOpen && isSearchable && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 0);
    }
  }, [isOpen, isSearchable]);

  useEffect(() => {
    optionRefs.current = optionRefs.current.slice(0, filteredOptions.length);
  }, [filteredOptions.length]);

  useEffect(() => {
    if (!isOpen || !triggerRef.current) return undefined;

    const updateMenuAlignment = () => {
      if (!triggerRef.current) return;

      const triggerRect = triggerRef.current.getBoundingClientRect();
      const menuWidth = menuRef.current?.offsetWidth || triggerRect.width;
      const wouldOverflowRight =
        triggerRect.left + menuWidth > window.innerWidth - 16;
      setAlignEnd(wouldOverflowRight);
    };

    updateMenuAlignment();
    window.addEventListener("resize", updateMenuAlignment);

    let resizeObserver;
    if (typeof ResizeObserver !== "undefined" && menuRef.current) {
      resizeObserver = new ResizeObserver(() => {
        updateMenuAlignment();
      });
      resizeObserver.observe(menuRef.current);
    }

    return () => {
      window.removeEventListener("resize", updateMenuAlignment);
      resizeObserver?.disconnect();
    };
  }, [isOpen, filteredOptions, isSearchable, searchInput, menuClass]);

  useEffect(() => {
    if (!isOpen || isSearchable || highlightedIndex < 0) return;
    optionRefs.current[highlightedIndex]?.focus();
  }, [highlightedIndex, isOpen, isSearchable]);

  const closeDropdown = ({ returnFocus = false } = {}) => {
    setIsOpen(false);
    setSearchInput("");
    setHighlightedIndex(-1);
    if (returnFocus) {
      setTimeout(() => triggerRef.current?.focus(), 0);
    }
  };

  const openDropdown = () => {
    setIsOpen(true);
    const selectedIndex = filteredOptions.findIndex(
      (option) => option.value === selected?.value,
    );
    setHighlightedIndex(selectedIndex >= 0 ? selectedIndex : 0);
  };

  const toggleDropdown = () => {
    if (isOpen) {
      closeDropdown();
      return;
    }
    openDropdown();
  };

  const handleOptionClick = (option) => {
    setSelected(option);
    closeDropdown({ returnFocus: true });
    onSelectRef.current?.(option.value);
  };

  const handleSearchChange = (e) => {
    setSearchInput(e.target.value);
    setHighlightedIndex(-1);
  };

  const handleKeyDown = (e) => {
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        if (!isOpen) {
          openDropdown();
          break;
        }
        setHighlightedIndex((prevIndex) =>
          prevIndex < filteredOptions.length - 1 ? prevIndex + 1 : 0,
        );
        break;

      case "ArrowUp":
        e.preventDefault();
        if (!isOpen) {
          openDropdown();
          break;
        }
        setHighlightedIndex((prevIndex) =>
          prevIndex > 0 ? prevIndex - 1 : filteredOptions.length - 1,
        );
        break;

      case "Enter":
        e.preventDefault();
        if (!isOpen) {
          openDropdown();
        } else if (highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
          handleOptionClick(filteredOptions[highlightedIndex]);
        }
        break;

      case " ":
        if (e.currentTarget === triggerRef.current) {
          e.preventDefault();
          toggleDropdown();
        }
        break;

      case "Escape":
        e.preventDefault();
        closeDropdown({ returnFocus: true });
        break;

      default:
        break;
    }
  };

  const optionTestId = (option) => {
    const raw = option?.value ?? option?.name ?? "";
    const slug = String(raw)
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
    return `${testId}-option-${slug || "option"}`;
  };

  return (
    <div className="relative w-full text-left" ref={dropdownRef} id={testId}>
      <div>
        <button
          ref={triggerRef}
          type="button"
          id={`${testId}-trigger`}
          onClick={toggleDropdown}
          onKeyDown={handleKeyDown}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-controls={`${testId}-menu`}
          className={`inline-flex w-full justify-between rounded-md px-4 py-2 text-sm font-medium text-cfDropdownSelectedText focus-visible:ring-2 focus-visible:ring-white/75 border ${buttonClass}`}
        >
          <span className="min-w-0 truncate">{selected?.name || ""}</span>
          <img src={DownArrow} alt="" className="ml-2 shrink-0" />
        </button>
      </div>

      {isOpen && (
        <div
          ref={menuRef}
          id={`${testId}-menu`}
          role="listbox"
          tabIndex={-1}
          aria-labelledby={`${testId}-trigger`}
          aria-activedescendant={
            highlightedIndex >= 0 && filteredOptions[highlightedIndex]
              ? optionTestId(filteredOptions[highlightedIndex])
              : undefined
          }
          className={`absolute mt-2 divide-y divide-gray-100 rounded-md bg-white shadow-lg ring-1 ring-black/5 focus:outline-none transition ease-out duration-100 transform opacity-100 scale-100 z-50 max-h-[300px] max-w-full overflow-y-auto overflow-x-hidden ${
            alignEnd ? "right-0 origin-top-right" : "left-0 origin-top-left"
          } ${menuClass}`}
        >
          {isSearchable && (
            <div className="sticky top-0 bg-white p-2 border-b border-gray-200">
              <input
                ref={searchInputRef}
                type="text"
                id={`${testId}-search-input`}
                role="combobox"
                aria-expanded={isOpen}
                aria-controls={`${testId}-menu`}
                aria-activedescendant={
                  highlightedIndex >= 0 && filteredOptions[highlightedIndex]
                    ? optionTestId(filteredOptions[highlightedIndex])
                    : undefined
                }
                placeholder={searchPlaceholder}
                value={searchInput}
                onChange={handleSearchChange}
                onKeyDown={handleKeyDown}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none"
              />
            </div>
          )}

          {filteredOptions.length > 0 ? (
            filteredOptions.map((option, index) => (
              <button
                ref={(element) => {
                  optionRefs.current[index] = element;
                }}
                type="button"
                key={option.value}
                id={optionTestId(option)}
                role="option"
                aria-selected={selected?.value === option.value}
                onClick={() => handleOptionClick(option)}
                onKeyDown={handleKeyDown}
                onMouseEnter={() => setHighlightedIndex(index)}
                onMouseLeave={() => setHighlightedIndex(-1)}
                className={`${
                  selected?.value === option.value
                    ? "bg-cfPrimaryButton text-white"
                    : highlightedIndex === index
                      ? "bg-gray-100 text-gray-900"
                      : "text-cfDropdownOptionsText hover:bg-gray-200"
                } group flex w-full min-w-0 items-center first:rounded-t-md last:rounded-b-md px-4 py-2 text-sm transition-colors duration-150`}
              >
                <span className="min-w-0 truncate text-left">
                  {option.name}
                </span>
              </button>
            ))
          ) : (
            <div
              id={`${testId}-empty`}
              className="px-4 py-3 text-sm text-gray-500 text-center"
            >
              No results found
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Dropdown;
